import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useState } from "react";
import { useResource, useFetcher } from "rest-hooks";
import { EncumberanceCheckResource } from "./EncumberanceCheckResource";
import { regNoWarnings } from "./encumberanceCheckMessages";
import { useIntl } from "react-intl";
import { IsRegNoEqual, NormalizeRegNo } from "./EncumberanceCheckParts";
import { willResetMval96 } from "./mval96";
const toEditableFields = (object) => ({
    regNo: object?.regNo ?? "",
    chassisNo: object?.chassisNo ?? "",
    machineRegNo: object?.machineRegNo ?? "",
    deliveryDate: object?.deliveryDate ?? "",
    identificationCode: object?.identificationCode ?? "",
    objectNameDescription: object?.objectNameDescription ?? "",
    portOfRegistry: object?.portOfRegistry ?? ""
});
function useEncumberanceCheckValue(applicationId, canResetDocs) {
    const currentObject = useResource(EncumberanceCheckResource.detail(), { applicationId });
    const searchByRegNo = useFetcher(EncumberanceCheckResource.searchByRegNo());
    const searchByChassisNo = useFetcher(EncumberanceCheckResource.searchByChassisNo());
    const checkEncumberance = useFetcher(EncumberanceCheckResource.checkEncumberance());
    const checkTitle = useFetcher(EncumberanceCheckResource.titleCheck());
    const save = useFetcher(EncumberanceCheckResource.save());
    const refetchCurrentObject = useFetcher(EncumberanceCheckResource.detail());
    const intl = useIntl();
    const [searchError, setSearchError] = useState(undefined);
    const [searchedRegNo, setSearchedRegNo] = useState("");
    const [searchResult, setSearchResult] = useState(undefined);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fields, setFields] = useState(() => toEditableFields(currentObject.object));
    const isVisible = (key) => currentObject.fieldVisibility?.[key] ?? false;
    const setField = (key) => (value) => setFields(prev => ({ ...prev, [key]: value }));
    const regNoValidationRegex = useMemo(() => {
        const pattern = currentObject.regNoRules?.validationPattern;
        if (!pattern)
            return null;
        try {
            return new RegExp(pattern);
        }
        catch {
            return null;
        }
    }, [currentObject.regNoRules?.validationPattern]);
    const isRegNoValid = (regNo) => {
        if (!regNoValidationRegex)
            return true;
        return regNoValidationRegex.test(regNo);
    };
    const doRegNoSearch = async (onSuccess, onFailure) => {
        setBusy(true);
        setSearchError(undefined);
        try {
            const result = await searchByRegNo({ applicationId, regNo: fields.regNo });
            if (result?.error) {
                if (result?.error === "GeneralError") {
                    setSearchError({ regNo: fields.regNo });
                }
                setSearchResult(undefined);
                await onFailure(result.error);
            }
            else {
                setSearchResult(result);
                setSearchedRegNo(fields.regNo);
                if (result?.object?.chassisNo)
                    setField("chassisNo")(result?.object?.chassisNo);
                await onSuccess(result);
            }
        }
        catch {
            setSearchResult(undefined);
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doChassisNoSearch = async (onSuccess, onFailure) => {
        setBusy(true);
        try {
            const result = await searchByChassisNo({ applicationId, chassisNo: fields.chassisNo });
            if (result?.error) {
                setSearchResult(undefined);
                await onFailure(result.error);
            }
            else {
                setSearchResult(result);
                if (result?.object?.regNo && isVisible("regNo")) {
                    setField("regNo")(result.object.regNo);
                    setSearchedRegNo(result.object.regNo);
                }
                await onSuccess(result);
            }
        }
        catch {
            setSearchResult(undefined);
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doEncumberanceCheck = async (onSuccess, onFailure) => {
        setBusy(true);
        try {
            const result = await checkEncumberance({ applicationId });
            const updated = await refetchCurrentObject({ applicationId });
            setFields(toEditableFields(updated?.object));
            if (result?.error)
                await onFailure(result.error);
            else
                await onSuccess(result);
        }
        catch {
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doTitleCheck = async (onSuccess, onFailure) => {
        setBusy(true);
        try {
            const result = await checkTitle({ applicationId });
            const updated = await refetchCurrentObject({ applicationId });
            setFields(toEditableFields(updated?.object));
            if (result?.error)
                await onFailure(result.error);
            else
                await onSuccess(result);
        }
        catch {
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
        }
    };
    const doSave = async (onPreSave, onPostSave, onSuccess, onFailure) => {
        setBusy(true);
        setSaving(true);
        try {
            if (!await onPreSave())
                return;
            const result = await save({ applicationId }, fields);
            if (!await onPostSave(result))
                return;
            const updated = await refetchCurrentObject({ applicationId });
            setFields(toEditableFields(updated?.object));
            if (result?.error) {
                await onFailure(result.error);
            }
            else {
                setSearchResult(prev => prev ? { ...prev, mismatch: result?.mismatch, mustReplace: result?.mustReplace } : prev);
                await onSuccess(result);
            }
        }
        catch {
            await onFailure(undefined);
        }
        finally {
            setBusy(false);
            setSaving(false);
        }
    };
    const applicationRegNo = NormalizeRegNo(currentObject.object?.regNo ?? "");
    const signedForRegNo = NormalizeRegNo(currentObject.regNoRules?.signedForRegNo ?? "");
    const signedForChassisNo = NormalizeRegNo(currentObject.regNoRules?.signedForChassisNo ?? "");
    const regNoChanged = isVisible("regNo") && !IsRegNoEqual(fields.regNo, applicationRegNo);
    const chassisNoChanged = isVisible("chassisNo") && !IsRegNoEqual(fields.chassisNo, currentObject.object?.chassisNo ?? "");
    const regNoCannotBeEmpty = fields.regNo.length === 0 && signedForRegNo.length > 0;
    const docsWillResetOnChange = signedForRegNo.length > 0 || (signedForChassisNo.length > 0 && chassisNoChanged);
    const docsWillResetOnSave = docsWillResetOnChange || willResetMval96(searchResult?.mismatch);
    const docsWillReset = ((regNoChanged && !IsRegNoEqual(fields.regNo, signedForRegNo)) || (chassisNoChanged && !IsRegNoEqual(fields.chassisNo, signedForChassisNo))) && docsWillResetOnSave;
    const hasSearchError = searchError !== undefined && IsRegNoEqual(fields.regNo, searchError.regNo);
    const searchValidationDisabled = currentObject.regNoRules?.searchValidationDisabled ?? false;
    const effectiveMustReplace = searchResult?.mustReplace ?? currentObject.mustReplace ?? false;
    const regNoSearchMissing = isVisible("regNoSearch") && !regNoCannotBeEmpty && fields.regNo.length > 0 && regNoChanged && !IsRegNoEqual(fields.regNo, searchedRegNo);
    const regNoNotValidated = isVisible("regNo") &&
        !regNoChanged &&
        !effectiveMustReplace &&
        !IsRegNoEqual(fields.regNo, searchedRegNo) &&
        currentObject.regNoSearchResult?.validationSuccess === false;
    const warnings = regNoWarnings(intl, {
        currentObject,
        searchResult,
        regNoEmpty: regNoCannotBeEmpty,
        regNoSearchMissing,
        searchValidationDisabled,
        regNoNotValidated,
        docsWillReset,
        docsWillResetOnSave,
        regNoChanged,
        searchedRegNo,
        regNoInput: fields.regNo,
        canResetDocs,
        hasSearchError
    });
    return {
        applicationId,
        currentObject,
        searchResult,
        isVisible,
        isRegNoValid,
        isBusy: busy,
        isSaving: saving,
        fields,
        setField,
        doRegNoSearch,
        doChassisNoSearch,
        doEncumberanceCheck,
        doTitleCheck,
        doSave,
        warnings,
        regNoRules: {
            regNoChanged,
            chassisNoChanged,
            regNoCannotBeEmpty,
            regNoSearchMissing: regNoSearchMissing && !searchValidationDisabled && !hasSearchError,
            docsWillResetOnChange,
            docsWillResetOnSave
        }
    };
}
const EncumberanceCheckContext = createContext(undefined);
export const useEncumberanceCheckContext = () => {
    const context = useContext(EncumberanceCheckContext);
    if (!context) {
        throw new Error("useEncumberanceCheckContext must be used within an EncumberanceCheckProvider");
    }
    return context;
};
export const EncumberanceCheckContextProvider = ({ children, applicationId, canResetDocs }) => {
    return (_jsx(EncumberanceCheckContext.Provider, { value: useEncumberanceCheckValue(applicationId, canResetDocs), children: children }));
};
//# sourceMappingURL=EncumberanceCheckContext.js.map